import React, { useState, useEffect } from 'react';
import { healthAPI } from '../api';
import { UserProfile, UpdateUserProfileRequest, LocationData, HealthEvent } from '../types';
import { LocationInput } from './LocationInput';
import { useUser } from '../contexts/UserContext';
import './UserContextPage.css'; // Reusing existing styles for now, can rename later

const FeedbackList: React.FC = () => {
    const [events, setEvents] = useState<HealthEvent[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadFeedback();
    }, []);

    const loadFeedback = async () => {
        setLoading(true);
        try {
            // We can't filter by multiple types in one call with current API structure effectively unless we iterate
            // But let's just fetch recent events and filter client side for now or make two calls?
            // Actually getEvents takes a single type. Let's make two calls to be safe.
            const [ideasResult, bugsResult] = await Promise.allSettled([
                healthAPI.getEvents({ type: 'idea' as any, limit: 10 }),
                healthAPI.getEvents({ type: 'bug' as any, limit: 10 })
            ]);

            const ideas = ideasResult.status === 'fulfilled' ? ideasResult.value.events : [];
            const bugs = bugsResult.status === 'fulfilled' ? bugsResult.value.events : [];

            if (ideasResult.status === 'rejected') console.error("Failed to load ideas", ideasResult.reason);
            if (bugsResult.status === 'rejected') console.error("Failed to load bugs", bugsResult.reason);

            // Merge and sort
            const all = [...ideas, ...bugs].sort((a, b) =>
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            setEvents(all);
        } catch (e) {
            console.error("Failed to load feedback history", e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ fontSize: '0.8em', color: '#888' }}>Loading history...</div>;
    if (events.length === 0) return <div style={{ fontSize: '0.8em', color: '#888' }}>No feedback submitted yet will show here.</div>;

    return (
        <div className="feedback-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {events.map(e => (
                <div key={e.trace_id} style={{
                    padding: '8px',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    fontSize: '0.85em',
                    borderLeft: `3px solid ${e.type === 'bug' ? '#dc3545' : '#ffc107'}`
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{e.type}</span>
                        <span style={{ color: '#999' }}>{new Date(e.timestamp).toLocaleDateString()}</span>
                    </div>
                    <div>{(e.metadata as any).text}</div>
                </div>
            ))}
        </div>
    );
};

export interface ProfilePageProps {
    onNavigate: (view: 'weight' | 'blood_pressure') => void;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ onNavigate }) => {
    const { profile, loading: profileLoading, refreshProfile } = useUser();
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Form states
    const [context, setContext] = useState<string>('');
    const [waterGoal, setWaterGoal] = useState<string>('2000');
    const [waterTrackingEnabled, setWaterTrackingEnabled] = useState<boolean>(true);
    const [waterUnit, setWaterUnit] = useState<'ml' | 'oz'>('ml');
    const [weightUnit, setWeightUnit] = useState<'kg' | 'lbs'>('lbs');
    const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [otherInputs, setOtherInputs] = useState<Array<{ name: string; type: any; default_dose?: string }>>([]);

    // Feedback form state
    const [feedbackType, setFeedbackType] = useState<'idea' | 'bug'>('idea');
    const [feedbackText, setFeedbackText] = useState<string>('');
    const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
    const [location, setLocation] = useState<LocationData | undefined>(undefined);

    // New Other Input state
    const [newMedName, setNewMedName] = useState('');
    const [newMedType, setNewMedType] = useState<'medication' | 'supplement' | 'other'>('medication');
    const [newMedDose, setNewMedDose] = useState('');

    useEffect(() => {
        if (profile) {
            setContext(profile.context);
            if (profile.settings) {
                setWaterGoal(profile.settings.daily_water_goal_ml?.toString() || '2000');
                setWaterTrackingEnabled(profile.settings.water_tracking_enabled ?? true);
                if (profile.settings.units) {
                    setWaterUnit(profile.settings.units.water || 'ml');
                    setWeightUnit(profile.settings.units.weight || 'lbs');
                }
                if (profile.settings.timezone) {
                    setTimezone(profile.settings.timezone);
                }
                // Support both new and legacy field names
                if (profile.settings.other_inputs) {
                    setOtherInputs(profile.settings.other_inputs);
                } else if (profile.settings.medications) {
                    setOtherInputs(profile.settings.medications);
                }
            }
        }
    }, [profile]);

    const handleAddOtherInput = () => {
        if (!newMedName.trim()) return;
        setOtherInputs(prev => [...prev, {
            name: newMedName.trim(),
            type: newMedType,
            default_dose: newMedDose.trim() || undefined
        }]);
        setNewMedName('');
        setNewMedDose('');
    };

    const handleRemoveOtherInput = (index: number) => {
        setOtherInputs(prev => prev.filter((_, i) => i !== index));
    };

    const handleFeedbackSubmit = async () => {
        if (!feedbackText.trim()) return;

        try {
            setSubmittingFeedback(true);
            setError(null);
            setSuccess(null);

            await healthAPI.createEvent({
                type: feedbackType,
                data: {
                    text: feedbackText,
                    location: location,
                }
            });

            setSuccess(`Thank you! Your ${feedbackType} has been recorded.`);
            setFeedbackText('');

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to submit feedback');
            console.error(err);
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const request: UpdateUserProfileRequest = {
                context,
                settings: {
                    daily_water_goal_ml: parseInt(waterGoal) || 2000,
                    water_tracking_enabled: waterTrackingEnabled,
                    units: {
                        water: waterUnit,
                        weight: weightUnit
                    },
                    timezone,
                    other_inputs: otherInputs
                }
            };

            await healthAPI.updateUserProfile(request);
            setSuccess('Profile updated successfully');

            // Reload to get updated version/timestamp
            await refreshProfile();
        } catch (err) {
            setError('Failed to save profile');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    if (profileLoading) return <div className="loading">Loading profile...</div>;

    return (
        <div className="user-context-page">
            <header className="page-header">
                <h2>👤 User Profile & Settings</h2>
                <div className="header-actions">
                    {/* Actions if needed */}
                </div>
            </header>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="profile-section manual-entry-section">
                <h3>📝 Manual Data Entry</h3>
                <p className="section-help">
                    Quickly log health measurements that are not automatically synced.
                </p>
                <div className="action-buttons">
                    <button
                        className="entry-button"
                        onClick={() => onNavigate('weight')}
                    >
                        ⚖️ Log Weight
                    </button>
                    <button
                        className="entry-button"
                        onClick={() => onNavigate('blood_pressure')}
                    >
                        ❤️ Log Blood Pressure
                    </button>
                </div>
            </div>

            <div className="profile-section settings-section">
                <h3>⚙️ Preferences</h3>

                <div className="setting-group">
                    <label>Timezone</label>
                    <select
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="timezone-select"
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                    >
                        {/* Common timezones */}
                        {[
                            "UTC",
                            "US/Pacific",
                            "US/Mountain",
                            "US/Central",
                            "US/Eastern",
                            "Europe/London",
                            "Europe/Paris",
                            "Asia/Tokyo",
                            "Australia/Sydney"
                        ].map(tz => (
                            <option key={tz} value={tz}>{tz}</option>
                        ))}
                        {/* If current timezone is not in common list, add it */}
                        {!["UTC", "US/Pacific", "US/Mountain", "US/Central", "US/Eastern", "Europe/London", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney"].includes(timezone) && (
                            <option value={timezone}>{timezone}</option>
                        )}
                        <option value="other" disabled>---</option>
                    </select>
                </div>

                <div className="setting-group">
                    <label>Water Tracking</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            type="checkbox"
                            checked={waterTrackingEnabled}
                            onChange={(e) => setWaterTrackingEnabled(e.target.checked)}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.9em', color: '#666' }}>
                            Enable water tracking (shows progress bar and AI summaries)
                        </span>
                    </div>
                </div>

                {waterTrackingEnabled && (
                    <div className="setting-group">
                        <label>Daily Water Goal (ml)</label>
                        <div className="input-with-unit">
                            <input
                                type="number"
                                value={waterGoal}
                                onChange={(e) => setWaterGoal(e.target.value)}
                            />
                            <span className="unit-label">ml</span>
                        </div>
                    </div>
                )}

                <div className="setting-group">
                    <label>Preferred Units</label>
                    <div className="unit-toggles">
                        <div className="toggle-group">
                            <span>Water:</span>
                            <div className="toggle-buttons">
                                <button
                                    className={waterUnit === 'ml' ? 'active' : ''}
                                    onClick={() => setWaterUnit('ml')}
                                >ml</button>
                                <button
                                    className={waterUnit === 'oz' ? 'active' : ''}
                                    onClick={() => setWaterUnit('oz')}
                                >oz</button>
                            </div>
                        </div>
                        <div className="toggle-group">
                            <span>Weight:</span>
                            <div className="toggle-buttons">
                                <button
                                    className={weightUnit === 'kg' ? 'active' : ''}
                                    onClick={() => setWeightUnit('kg')}
                                >kg</button>
                                <button
                                    className={weightUnit === 'lbs' ? 'active' : ''}
                                    onClick={() => setWeightUnit('lbs')}
                                >lbs</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-section medications-section">
                <h3>📝 Other Inputs</h3>
                <p className="section-help">
                    Manage medications, supplements, and other items that appear in your quick-add menu.
                </p>

                <div className="medication-list" style={{ marginBottom: '20px' }}>
                    {otherInputs.length === 0 ? (
                        <div style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em' }}>No items added yet.</div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {otherInputs.map((item, idx) => (
                                <div key={idx} className="medication-tag" style={{
                                    background: '#f0f4ff',
                                    border: '1px solid #cce0ff',
                                    padding: '6px 12px',
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '0.9em'
                                }}>
                                    <span>{item.name} {item.default_dose ? `(${item.default_dose})` : ''}</span>
                                    <button
                                        onClick={() => handleRemoveOtherInput(idx)}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#888', fontWeight: 'bold' }}
                                    >×</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="add-medication-form" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    padding: '15px',
                    background: '#f9f9f9',
                    borderRadius: '8px'
                }}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            placeholder="Item name (e.g. Psyllium, Vitamin D)..."
                            value={newMedName}
                            onChange={(e) => setNewMedName(e.target.value)}
                            style={{ flex: 2, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        <select
                            value={newMedType}
                            onChange={(e) => setNewMedType(e.target.value as any)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                            <option value="medication">💊 Medication</option>
                            <option value="supplement">🌿 Supplement</option>
                            <option value="other">❓ Other</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            placeholder="Default dose (e.g. 1 pill, 500mg)"
                            value={newMedDose}
                            onChange={(e) => setNewMedDose(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        />
                        <button
                            onClick={handleAddOtherInput}
                            disabled={!newMedName.trim()}
                            style={{
                                padding: '8px 20px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >Add</button>
                    </div>
                </div>
            </div>

            <div className="profile-section feedback-section">
                <h3>💡 Ideas & Bugs</h3>
                <p className="section-help">
                    Capture ideas for new features or report bugs you encounter.
                </p>
                <div className="feedback-form">
                    <div className="feedback-type-toggle">
                        <button
                            className={`type-btn ${feedbackType === 'idea' ? 'active idea' : ''}`}
                            onClick={() => setFeedbackType('idea')}
                        >
                            💡 Idea
                        </button>
                        <button
                            className={`type-btn ${feedbackType === 'bug' ? 'active bug' : ''}`}
                            onClick={() => setFeedbackType('bug')}
                        >
                            🐞 Bug
                        </button>
                    </div>
                    <textarea
                        className="feedback-input"
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={`Describe your ${feedbackType}...`}
                        rows={3}
                    />

                    <LocationInput onLocationChange={setLocation} />

                    <button
                        className="submit-feedback-btn"
                        onClick={handleFeedbackSubmit}
                        disabled={!feedbackText.trim() || submittingFeedback}
                    >
                        {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                </div>

                {/* Feedback History - Added as part of profile cleanup */}
                <div className="feedback-history" style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                    <h4 style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>Your Feedback History</h4>
                    <FeedbackList />
                </div>
            </div>



            <div className="profile-section context-section">
                <h3>🧠 User AI Context</h3>
                <p className="section-help">
                    This context acts as the "system prompt" for the AI when analyzing your health data.
                    Update this with your medical history, goals, and specific things you want the AI to look for.
                </p>
                <textarea
                    className="context-editor"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="# Personal Health Context..."
                />
            </div>

            <div className="page-actions">
                <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Profile'}
                </button>
                {/* 
                  Only show the timestamp if profile?.updated_at is present.
                  Also check if profile exists, though the button logic relies on 'saving'.
                */}
                {profile?.updated_at && (
                    <span className="last-updated">
                        Last updated: {new Date(profile.updated_at).toLocaleString()} (v{profile.version})
                    </span>
                )}
            </div>

            <style>{`
                .profile-section {
                    background: white;
                    padding: 20px;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    margin-bottom: 20px;
                }
                .section-help {
                    color: #666;
                    font-size: 0.9em;
                    margin-bottom: 15px;
                    line-height: 1.4;
                }
                .manual-entry-section .action-buttons {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }
                .entry-button {
                    background: #f0f4ff;
                    border: 1px solid #cce0ff;
                    color: #0056b3;
                    padding: 12px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                .entry-button:hover {
                    background: #e6f0ff;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .entry-button:active {
                    transform: translateY(0);
                }
                .setting-group {
                    margin-bottom: 15px;
                }
                .setting-group label {
                    display: block;
                    font-weight: 500;
                    margin-bottom: 5px;
                    color: #444;
                }
                .input-with-unit {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .unit-toggles {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }
                .toggle-group {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                }
                .toggle-buttons {
                    display: flex;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    overflow: hidden;
                }
                .toggle-buttons button {
                    border: none;
                    background: #f5f5f5;
                    padding: 6px 14px;
                    cursor: pointer;
                    font-size: 0.9em;
                    transition: background 0.2s;
                }
                .toggle-buttons button.active {
                    background: #007bff;
                    color: white;
                }
                @media (max-width: 480px) {
                    .manual-entry-section .action-buttons {
                        grid-template-columns: 1fr;
                    }
                }
                .feedback-section .feedback-form {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }
                .feedback-type-toggle {
                    display: flex;
                    gap: 10px;
                }
                .type-btn {
                    flex: 1;
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    background: #f9f9f9;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s;
                }
                .type-btn.active.idea {
                    background: #e6f0ff;
                    border-color: #007bff;
                    color: #007bff;
                }
                .type-btn.active.bug {
                    background: #ffe6e6;
                    border-color: #dc3545;
                    color: #dc3545;
                }
                .feedback-input {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-family: inherit;
                    resize: vertical;
                }
                .feedback-input:focus {
                    outline: none;
                    border-color: #007bff;
                    box-shadow: 0 0 0 2px rgba(0,123,255,0.1);
                }
                .submit-feedback-btn {
                    padding: 10px;
                    background: #28a745;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                    align-self: flex-end;
                }
                .submit-feedback-btn:hover:not(:disabled) {
                    background: #218838;
                }
                .submit-feedback-btn:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }
            `}</style>
        </div >
    );
};
